import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import qs from 'qs';

import { getInjector, getRootScope } from '@/services/angularjs-bridge';
import { restUrl } from '@/services/utils';

import {
    ContactRequestRow,
    EmailInvitationRecord,
    InvitationContactRecord,
    mergeContactRequests,
} from './invitation';

const repeatParams = (params: Record<string, unknown>) => qs.stringify(params, { arrayFormat: 'repeat' });

const displayUserName = (userId?: string): string => {
    if (!userId) {
        return '';
    }
    try {
        const name = getRootScope(getInjector()).getUserName?.(userId);
        return name || userId;
    } catch {
        return userId;
    }
};

const invalidateInvitations = async (queryClient: ReturnType<typeof useQueryClient>) => {
    await queryClient.invalidateQueries({ queryKey: ['invitations'] });
};

export const usePendingInvitations = () =>
    useQuery({
        queryKey: ['invitations', 'pending'],
        queryFn: async () => {
            const response = await axios.get<InvitationContactRecord[]>(restUrl('contacts'), {
                params: { state: 'PENDING' },
            });
            return response.data ?? [];
        },
    });

export const useContactRequests = () =>
    useQuery({
        queryKey: ['invitations', 'requests'],
        queryFn: async (): Promise<ContactRequestRow[]> => {
            const [emails, contacts] = await Promise.all([
                axios.get<EmailInvitationRecord[]>(restUrl('invitations'), {
                    params: { type: ['INVITED_BUYER', 'INVITED_COMMUNITY_MEMBER', 'INVITED_SUPPLIER'] },
                    paramsSerializer: repeatParams,
                }),
                axios.get<InvitationContactRecord[]>(restUrl('contacts'), {
                    params: { state: ['INVITED', 'REJECTED', 'ESTABLISHED'] },
                    paramsSerializer: repeatParams,
                }),
            ]);
            return mergeContactRequests(emails.data ?? [], contacts.data ?? [], displayUserName);
        },
    });

export const useProcessInvitation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ organizationId, action }: { organizationId: number; action: 'accept' | 'reject' }) =>
            axios.post(restUrl(`contacts/${organizationId}/${action}`)),
        onSettled: () => invalidateInvitations(queryClient),
    });
};

export const useSaveEmailInvitation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (invitation: EmailInvitationRecord) => axios.post(restUrl('invitations'), invitation),
        onSuccess: () => invalidateInvitations(queryClient),
    });
};

export const useDeleteEmailInvitation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (invitationId: number) => axios.delete(restUrl(`invitations/${invitationId}`)),
        onSuccess: () => invalidateInvitations(queryClient),
    });
};

export const useSaveContactRequest = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (contact: { id?: number; customValues?: Record<string, unknown>; organizationId?: number }) =>
            axios.put(restUrl('contacts'), contact),
        onSuccess: () => invalidateInvitations(queryClient),
    });
};
